import {
  Divider,
  IconButton,
  TableCell,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { Link } from '@src/components/Link'
import ListError from '@src/components/ListError'
import PageSkeleton from '@src/components/PageSkeleton'
import { orderCenterRoutes } from '@src/routes'
import { WebOrdersComponent } from '@src/queries/WebOrdersQuery.generated'
import { Sort, Order, OrderItemTrackingInfo } from '@src/types/graphql-types'
import messages from '@src/routes/OrderCenter/messages'
import {
  GuestShoppingType,
  SitePreference,
  useCurrentUser,
} from '@utils/useCurrentUser'
import { useUserSession } from '@utils/useUserSession'
import React, { FC, useState } from 'react'
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from 'react-intl'
import ItemsSkeleton from '../ItemsSkeleton'
import OrderCenterLayout from '../OrderCenterLayout'
import DataTable from '@src/components/DataTable'
import OrderStatus from '../OrderStatus'
import SearchIcon from '@src/icons/SearchIcon'
import ResponsiveDataAccordion from '@src/components/ResponsiveDataAccordion/ResponsiveDataAccordion'
import { DataColumn } from '@src/components/DataTable/DataTable'
import { useCompanyName } from '@src/utils/useCompanyName'
import { renderOrderTypeText } from '@src/utils/renderOrderTypeText'
import AccountAffiliation from '@src/components/AccountAffiliation'
import InfoIcon from '@material-ui/icons/Info'
import { sendOrderStatusInfoTracking } from '@src/utils/analytics'
import { OrderStatusInfoEvent } from '@utils/analytics/types'
import { sendOrderCenterEvent } from '@src/routes/OrderCenter/analytics'
import CaretDownIcon from '@src/icons/CaretDownIcon'
import clsx from 'clsx'
import { OrderRowExpansion } from '../OrderSearch/OrderRowExpansion'
import { OrderDetailTrackingModal } from '../OrderDetail/OrderDetailTrackingModal'
import { getShipperCodeByTrackingUrl } from '@src/utils/orderTrackingInfoMappings'
import { without } from 'lodash'
import { MobileOrderItemList } from '../OrderSearch/MobileOrderItemList'
import OrderStatusModal from '@src/components/OrderStatusModal/OrderStatusModal'
import { shouldShowAccountAffiliationForm } from '@src/utils/orderCenterUtils'
import { AffiliationType } from '@src/components/AccountAffiliation/AccountAffiliation'
import countriesMap from '@localization/countries.json'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'

const RECENT_ORDERS_DEFAULT = 15

const useStyles = makeStyles((theme: Theme) => ({
  beforeTable: {
    display: 'inline-block',
    margin: theme.spacing(4, 0, 4),
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.up('md')]: {
      backgroundColor: 'transparent',
      borderBottom: 'none',
      margin: theme.spacing(0, 0, 6.5, 0),
    },
  },
  search: {
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    fontSize: theme.typography.pxToRem(12),
    verticalAlign: 'middle',
    marginRight: theme.spacing(2),
  },
  hideOnMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  error: {
    padding: theme.spacing(6, 4),
    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  table: {
    '& .MuiTableRow-root.Mui-selected': {
      backgroundColor: theme.palette.background.grey,
    },
  },
  rowExpansion: {
    '&.MuiTableRow-root': {
      borderTop: 'none',
      backgroundColor: theme.palette.background.grey,
    },
    '& > .MuiTableCell-root': {
      padding: theme.spacing(0, 3, 6),
    },
  },
  caretButton: {
    aspectRatio: '1',
  },
  caret: {
    fontSize: theme.typography.pxToRem(10),
    verticalAlign: 'middle',
  },
  caretExpanded: {
    transform: 'rotate(180deg)',
  },
  divider: {
    marginTop: theme.spacing(4),
  },
  orderItemsSection: {
    margin: theme.spacing(6, 0, 2),
  },
  infoIconButton: {
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(-3, -1, -2),
    },
  },
}))

const WebOrders: FC<{}> = () => {
  const classes = useStyles()
  const theme = useTheme()
  const companyName = useCompanyName()
  const { userSession } = useUserSession()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true
  const noResultsComponent = (
    <FormattedMessage id="NO_RESULTS_FOUND" defaultMessage="No results found" />
  )
  const [modal, setModal] = useState<'order-status' | null>(null)
  const { formatMessage } = useIntl()
  const { userId = '' } = useCurrentUser()
  const [trackingInfo, setTrackingInfo] =
    useState<OrderItemTrackingInfo | null>(null)
  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)
  const [expandedOrders, setExpandedOrders] = useState<string[]>([])
  const isExpanded = (order: Order): boolean =>
    Boolean(
      order.salesDocNumber && expandedOrders.includes(order.salesDocNumber)
    )

  const sendOrderItemsToggleEvent = (willExpand: boolean) =>
    sendOrderCenterEvent(
      `${willExpand ? 'expand' : 'collapse'} order items`,
      userId
    )
  const toggleRow = (order: Order) => {
    if (!order.salesDocNumber) return
    const expanded = isExpanded(order)
    setExpandedOrders(
      expanded
        ? without(expandedOrders, order.salesDocNumber)
        : [...expandedOrders, order.salesDocNumber]
    )
    sendOrderItemsToggleEvent(!expanded)
  }

  const {
    getSitePreference,
    currentUser,
    currentUserLoading,
    isRegisteredCustomer,
    isOrderStatusCustomer,
    isDTAQZuCustomer,
    uniqueSoldTos,
    userIsLoggedIn,
  } = useCurrentUser()
  const guestShoppingType = String(
    currentUser?.__typename === 'LoggedInUser' &&
      currentUser?.metadata?.guestShoppingType
  )

  const showAccountAffiliationForm = shouldShowAccountAffiliationForm(
    currentUser,
    userSession,
    uniqueSoldTos,
    userIsLoggedIn
  )

  if (currentUserLoading) {
    return <PageSkeleton />
  }

  // TODO Show unauthorized page
  if (!isRegisteredCustomer) return null
  const dealerCountryRoleEmpty =
    (guestShoppingType === GuestShoppingType.Dealer &&
      !isOrderStatusCustomer) ||
    (guestShoppingType === GuestShoppingType.DTAQ &&
      !isOrderStatusCustomer &&
      !isDTAQZuCustomer) ||
    isDTAQZuCustomer
  const numberOfRecentOrders =
    getSitePreference(SitePreference.NumberOfRecentOrders) ||
    RECENT_ORDERS_DEFAULT

  const handleOrderStatusInfoClick = (
    modal: 'order-status' | null,
    action: OrderStatusInfoEvent
  ) => {
    setModal(modal)
    sendOrderStatusInfoTracking(action, userId)
  }

  return (
    <OrderCenterLayout isLogoEnabled>
      <WebOrdersComponent
        variables={{
          input: {
            numberOfRecentOrders,
          },
        }}
        context={{ userSession }}
        ssr={false}
        fetchPolicy="no-cache"
      >
        {({ loading, error, data }) => {
          if (loading) return <ItemsSkeleton />

          if (error || !data || !data.webOrders)
            return (
              <div className={classes.error}>
                <ListError />
              </div>
            )

          const orders: Order[] = data.webOrders.orders
          const hasOrders = orders?.length > 0

          const tableColumns: DataColumn<Order>[] = [
            {
              headElement: <FormattedMessage {...messages.ORDER} />,
              bodyElement: (order, noLink?) => {
                const salesDocNumberLink = order.orderId
                  ? `${orderCenterRoutes.orderDetail(
                      order.orderId
                    )}?returnUrl=${encodeURIComponent(
                      orderCenterRoutes.orders()
                    )}`
                  : `${orderCenterRoutes.orderDetail(
                      order.salesDocNumber!
                    )}?returnUrl=${encodeURIComponent(
                      `${orderCenterRoutes.orderSearch()}`
                    )}`

                if (noLink) {
                  return order.salesDocNumber
                }

                return (
                  <Link href={salesDocNumberLink} passHref>
                    <a
                      data-testid={`order-detail-${order.salesDocNumber}`}
                      id={`order-detail-${order.salesDocNumber}`}
                    >
                      {order.salesDocNumber}
                    </a>
                  </Link>
                )
              },
              width: '15%',
              sortBy: 'salesDocNumber',
            },
            {
              headElement: (
                <FormattedMessage id="CREATED" defaultMessage="Created" />
              ),
              bodyElement: (order) => (
                <FormattedDate
                  value={new Date(order.orderDate)}
                  year="numeric"
                  month="long"
                  day="numeric"
                />
              ),
              width: dealerCountryRoleEmpty ? '20%' : '15%',
              sortBy: 'orderDate',
            },
            {
              headElement: (
                <FormattedMessage id="PO_NUMBER" defaultMessage="PO Number" />
              ),
              bodyElement: (order) => order.poNumber,
              width: dealerCountryRoleEmpty ? '20%' : '15%',
              dataScrub: true,
              sortBy: 'poNumber',
            },
            {
              headElement: <FormattedMessage {...messages.ORDERED_BY} />,
              bodyElement: (order) => order.orderedBy,
              width: '15%',
              dataScrub: true,
              sortBy: 'orderedBy',
            },
          ]

          if (currentUser?.metadata?.isMarketplaceCartEnabled) {
            tableColumns.push({
              headElement: <FormattedMessage {...messages.ORDERED_TYPE} />,
              bodyElement: (order) => {
                return renderOrderTypeText(companyName, order.orderType)
              },
              width: '15%',
              dataScrub: true,
              sortBy: 'orderType',
            })
          }

          if (!dealerCountryRoleEmpty) {
            tableColumns.splice(1, 0, {
              headElement: (
                <FormattedMessage id="STATUS" defaultMessage="Status" />
              ),
              headElementEndIcon: (
                <IconButton
                  className={classes.infoIconButton}
                  aria-label={formatMessage(messages.DETAILS)}
                  onClick={() =>
                    handleOrderStatusInfoClick('order-status', 'open')
                  }
                >
                  <InfoIcon color="primary" />
                </IconButton>
              ),
              bodyElement: (order) => <OrderStatus order={order} />,
              width: '15%',
              dataScrub: false,
              sortBy: 'status',
            })
          }
          if (!isDTAQZuCustomer) {
            tableColumns.push({
              headElement: (
                <FormattedMessage id="TOTAL" defaultMessage="Total" />
              ),
              bodyElement: (order) =>
                order.quotePriceItemExists ? (
                  <FormattedMessage {...messages.QUOTE_ITEM} />
                ) : order.total >= 0 ? (
                  <FormattedNumber
                    value={order.total}
                    style="currency"
                    currency={order.currency}
                    currencyDisplay="symbol"
                  />
                ) : (
                  <span />
                ),
              width: '15%',
              textAlign: 'right',
              sortBy: 'total',
            })
          }
          if (isDesktop) {
            tableColumns.push({
              width: 36,
              textAlign: 'center',
              headElement: (
                <Typography variant="srOnly">
                  <FormattedMessage {...messages.ORDER_DETAILS} />
                </Typography>
              ),
              bodyElement: (order) => {
                const expanded = isExpanded(order)
                return (
                  <IconButton
                    size="small"
                    className={classes.caretButton}
                    aria-label={formatMessage(
                      expanded ? messages.SHOW_LESS : messages.SHOW_MORE
                    )}
                    aria-live="polite"
                    onClick={() => toggleRow(order)}
                  >
                    <CaretDownIcon
                      className={clsx(classes.caret, {
                        [classes.caretExpanded]: expanded,
                      })}
                    />
                  </IconButton>
                )
              },
            })
          }

          const resultsComponent = (
            <>
              {isDesktop ? (
                <DataTable
                  highlightRow
                  verticalAlign={'top'}
                  data={orders}
                  columns={tableColumns}
                  captionText={<FormattedMessage {...messages.ORDERS} />}
                  noDataComponent={noResultsComponent}
                  initialSortColumn={'orderDate'}
                  initialSortDirection={Sort.Namedesc}
                  rowSelected={isExpanded}
                  onRowClick={(order, e) => {
                    const clickedEl = (e.target as Element).closest(
                      'tr, button, a'
                    )
                    if (clickedEl?.tagName === 'TR') toggleRow(order)
                  }}
                  className={classes.table}
                  keyExtractor={(order, i) => order.salesDocNumber || i}
                  afterRow={(order) =>
                    !isExpanded(order) ? null : (
                      <TableRow
                        className={classes.rowExpansion}
                        data-testid={`row-expansion-${order.salesDocNumber}`}
                      >
                        <TableCell colSpan={tableColumns.length}>
                          <OrderRowExpansion
                            order={order}
                            setTrackingInfo={setTrackingInfo}
                            userId={userId}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  }
                />
              ) : (
                <ResponsiveDataAccordion<Order>
                  data={orders}
                  columns={tableColumns}
                  accordionTitleColumn={tableColumns[0]}
                  accordionSubtitleColumn={tableColumns[2]}
                  noResultsComponent={noResultsComponent}
                  itemProps={{
                    onToggle: sendOrderItemsToggleEvent,
                    renderFooter: (order, isExpanded) => (
                      <>
                        <Divider className={classes.divider} />
                        <div className={classes.orderItemsSection}>
                          <MobileOrderItemList
                            order={order}
                            setTrackingInfo={setTrackingInfo}
                            skipQuery={!isExpanded}
                            userId={userId}
                          />
                        </div>
                      </>
                    ),
                  }}
                />
              )}
              <OrderStatusModal
                open={modal === 'order-status'}
                onClose={() => handleOrderStatusInfoClick(null, 'close')}
              />
            </>
          )

          return (
            <>
              <div className={classes.beforeTable}>
                {showAccountAffiliationForm && !hasOrders ? (
                  <AccountAffiliation
                    affiliationType={AffiliationType.Orders}
                    hasItems={hasOrders}
                  />
                ) : (
                  <>
                    {isOrderStatusCustomer ? (
                      <>
                        <Link href={orderCenterRoutes.orderSearch()}>
                          <a
                            className={classes.search}
                            href={orderCenterRoutes.orderSearch()}
                          >
                            <SearchIcon className={classes.searchIcon} />
                            <FormattedMessage {...messages.ORDER_SEARCH} />
                          </a>
                        </Link>
                      </>
                    ) : null}
                  </>
                )}
              </div>
              {hasOrders || !showAccountAffiliationForm
                ? resultsComponent
                : null}
            </>
          )
        }}
      </WebOrdersComponent>
      {isChinaUser && (
        <OrderDetailTrackingModal
          trackingInfo={trackingInfo}
          open={trackingInfo !== null}
          onClose={() => setTrackingInfo(null)}
          shipperCode={getShipperCodeByTrackingUrl(trackingInfo?.trackingUrl)}
        />
      )}
    </OrderCenterLayout>
  )
}

export default WebOrders
